import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Archive, ArchiveRestore, CheckSquare, Filter, LayoutDashboard, List, ListChecks, X } from 'lucide-react';
import { formatDateUTC, isValidDate } from '@/common/utils/date';
import { useEffect, useMemo, useState, useRef } from 'react';
import toast from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import { Link as ReactRouterLink } from 'react-router-dom';
import { useAlert } from '../../hooks/useAlert';
import { useTramites } from '@/hooks/useTramites';
import AppLayout from '../Layouts/AppLayout';
import { useAuth } from '@/auth';
import { canArchivarEscrituras, checkAllPermissions, PERMISSIONS } from '@/auth/permissions';
import { updateEstadoTramite } from '../../services/tramiteService';
import ResultsCounter from '../ResultsCounter';
import Paginator from '../Paginator';
import TramitesFichas from '../TramitesFichas';
import TramitesTable from '../TramitesTable';
import { ColumnToggle } from '../ColumnToggle';
import FiltersModal from '../FiltersModal';
import { Search } from 'lucide-react';
import { FILTERS } from '@/common/constants';
import type { UserOption } from '@/otros-tramites/services/otrosTramitesService';
import { partialUpdateTramite } from '@/services/tramiteService';
import { useToggleBloqueoTramite } from '@/hooks/useToggleBloqueoTramite';
import { BloqueoTramiteDialog } from '@/components/BloqueoTramiteDialog';
import { ArchivarTramiteDialog } from '@/components/ArchivarTramiteDialog';
import { useArchivarTramite } from '@/hooks/useArchivarTramite';
import {
  getEscrituraKanbanStatus,
  patchKanbanTramite,
  relocateKanbanTramite,
  removeKanbanTramite,
  resetKanbanBoard,
  toKanbanPatch,
  TramitesKanban,
} from '@/components/TramitesKanban';
import type { KanbanFilters } from '@/components/TramitesKanban';
import { usePreferences } from '@/auth/hooks/usePreferences';
import { TRAMITE_TAREAS_ROUTES } from '@/tramite-tareas';

type TramitesViewMode = 'table' | 'kanban';

const Home = () => {

  const { user } = useAuth();
  const userPermissions = user?.permissions || [];
  const canManageTramites = checkAllPermissions(userPermissions, [PERMISSIONS.MANAGE_TRAMITES.name]);
  const canViewTramiteStatus = checkAllPermissions(userPermissions, [PERMISSIONS.VIEW_TRAMITE_STATUS.name]);
  const canSeeStatus = canManageTramites || canViewTramiteStatus;

  // Ref to track if filters are being applied to avoid double fetch
  const isApplyingFilters = useRef(false);

  const [isLargerThan1280, setIsLargerThan1280] = useState(window.innerWidth >= 1280);
  const [filtersModalOpen, setFiltersModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState<TramitesViewMode>('table');
  const isKanbanView = viewMode === 'kanban';
  const queryClient = useQueryClient();
  const {
    preferences,
    isLoading: preferencesLoading,
    isError: preferencesFailed,
    updatePartialPreference,
  } = usePreferences();
  const {
    tramites,
    loading,
    isReady,
    setSearchText,
    searchText,
    activeSearchText,
    fetchDataTramites,
    executeSearch,
    applyFilters,
    resetFilters,
    links,
    currentPage,
    totalItems,
    from,
    to,
    sortField,
    sortDirection,
    setSortField,
    setSortDirection,
    dateFrom,
    dateTo,
    dateField,
    setDateFrom,
    setDateTo,
    setDateField,
    escrituraFilter,
    setEscrituraFilter,
    registroFilter,
    setRegistroFilter,
    estadoFilter,
    setEstadoFilter,
    bloqueadaFilter,
    setBloqueadaFilter,
    archivadoFilter,
    assignedUserId,
    assignedUser,
  } = useTramites(userPermissions, { enabled: !isKanbanView });

  const canManageBloqueo = checkAllPermissions(userPermissions, [PERMISSIONS.MANAGE_BLOQUEO_TRAMITE.name]);
  const canArchivar = canArchivarEscrituras(userPermissions);

  // El kanban trae sus propios datos por columna; comparte el resto de los filtros
  const kanbanFilters: KanbanFilters = useMemo(() => ({
    search: activeSearchText,
    dateFrom,
    dateTo,
    dateField,
    registroFilter,
    estadoFilter,
    // Sin el permiso el filtro no se ofrece ni se cuenta como activo, así que
    // tampoco tiene que viajar al backend.
    bloqueadaFilter: canManageBloqueo ? bloqueadaFilter : 'todos',
    // Sin el permiso las archivadas simplemente no se ven: el filtro no se
    // ofrece y el backend las oculta por defecto.
    archivadoFilter: canArchivar ? archivadoFilter : '',
    assignedUserId,
  }), [activeSearchText, dateFrom, dateTo, dateField, registroFilter, estadoFilter, bloqueadaFilter, archivadoFilter, assignedUserId, canManageBloqueo, canArchivar]);

  /** Aplica en la tarjeta del tablero lo que devolvió el endpoint que la editó. */
  const applyTramiteUpdate = (tramiteId: number, updated: unknown) => {
    if (!isKanbanView) {
      fetchDataTramites();
      return;
    }
    patchKanbanTramite(queryClient, tramiteId, toKanbanPatch(updated));
  };

  /**
   * El bloqueo es una columna del tablero, así que además de actualizar los
   * datos hay que mover la tarjeta: bloquear la manda a "Bloqueada" y
   * desbloquear la devuelve a la que le corresponde por su avance.
   */
  const applyBloqueoUpdate = (tramiteId: number, updated: any) => {
    applyTramiteUpdate(tramiteId, updated);

    if (isKanbanView) {
      void relocateKanbanTramite(
        queryClient,
        tramiteId,
        getEscrituraKanbanStatus(updated),
      );
    }
  };

  // Temporary filter states (used in modal before applying)
  const [tempDateFrom, setTempDateFrom] = useState(dateFrom);
  const [tempDateTo, setTempDateTo] = useState(dateTo);
  const [tempDateField, setTempDateField] = useState(dateField);
  const [tempEscrituraFilter, setTempEscrituraFilter] = useState(escrituraFilter);
  const [tempRegistroFilter, setTempRegistroFilter] = useState(registroFilter);
  const [tempEstadoFilter, setTempEstadoFilter] = useState(estadoFilter);
  const [tempBloqueadaFilter, setTempBloqueadaFilter] = useState(bloqueadaFilter);
  const [tempArchivadoFilter, setTempArchivadoFilter] = useState(archivadoFilter);
  const [tempAssignedUser, setTempAssignedUser] = useState<UserOption | null>(null);
  const { questionAlert, successAlert, errorAlert } = useAlert();
  const [bloqueoDialogTramite, setBloqueoDialogTramite] = useState<any | null>(null);
  const { toggle: toggleBloqueo } = useToggleBloqueoTramite();
  const [archivarDialogTramite, setArchivarDialogTramite] = useState<any | null>(null);
  const [archivarLoteAbierto, setArchivarLoteAbierto] = useState(false);
  const { toggle: toggleArchivado, toggleMasivo: archivarMasivo, isSubmitting: isArchivando } = useArchivarTramite();

  // Modo selección: los checkboxes de la tabla sólo aparecen con esto activo.
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (preferencesLoading) return;

    // Si las preferencias no cargaron, no se sabe qué vista eligió el usuario.
    // Forzar 'table' acá le cambiaba la vista sin avisarle y sin poder deshacerlo.
    if (preferencesFailed) return;

    const savedViewMode = preferences?.tramites?.viewMode;
    setViewMode(savedViewMode === 'kanban' ? 'kanban' : 'table');
  }, [preferences?.tramites?.viewMode, preferencesLoading, preferencesFailed]);

  // Aviso único: el usuario tiene que saber que está viendo filtros por defecto
  // y no los suyos, y poder reintentar.
  const hasWarnedAboutPreferences = useRef(false);
  useEffect(() => {
    if (!preferencesFailed || hasWarnedAboutPreferences.current) return;
    hasWarnedAboutPreferences.current = true;

    toast.error(
      'No pudimos cargar tus filtros y tu vista guardados. Estás viendo la configuración por defecto.',
      {
        id: 'preferences-load-failed',
        duration: 8000,
      },
    );
  }, [preferencesFailed]);

  const handleViewModeChange = (mode: TramitesViewMode) => {
    setViewMode(mode);
    // El kanban no tiene checkboxes: salir del modo evita quedar con una
    // selección invisible al volver a la tabla.
    if (mode === 'kanban') {
      exitSelectionMode();
    }
    updatePartialPreference('tramites.viewMode', mode);
  };

  useEffect(() => {
    const handleResize = () => {
      setIsLargerThan1280(window.innerWidth >= 1280);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSearchClick = () => {
    executeSearch();
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      executeSearch();
    }
  }

  const handleSortChanged = (field, direction) => {
    setSortField(field);
    setSortDirection(direction);
  }

  // Temporary filter handlers (for modal)
  const handleTempDateFromChange = (date) => {
    if (!date) {
      setTempDateFrom('');
      return;
    }
    const formattedDate = formatDateUTC(date);
    // Validate the formatted date to prevent "Invalid Date"
    if (formattedDate === 'Invalid Date' || !isValidDate(formattedDate)) {
      setTempDateFrom('');
    } else {
      setTempDateFrom(formattedDate);
    }
  }

  const handleTempDateToChange = (date) => {
    if (!date) {
      setTempDateTo('');
      return;
    }
    const formattedDate = formatDateUTC(date);
    // Validate the formatted date to prevent "Invalid Date"
    if (formattedDate === 'Invalid Date' || !isValidDate(formattedDate)) {
      setTempDateTo('');
    } else {
      setTempDateTo(formattedDate);
    }
  }

  const handleTempDateFieldChange = (field) => {
    setTempDateField(field);
  }

  const handleClearTempDateFilter = () => {
    // Check if user has the permission to filter tramites by date
    const hasDateFilterPermission = userPermissions.includes(PERMISSIONS.FILTER_BY_DATE.name);

    setTempDateFrom(hasDateFilterPermission ? FILTERS.DATE_FROM_DEFAULT : '');
    setTempDateTo('');
    setTempDateField('fecini');
  }

  const handleTempEscrituraFilterChange = (filter) => {
    setTempEscrituraFilter(filter);
  }

  const handleClearTempEscrituraFilter = () => {
    setTempEscrituraFilter('todas');
  }

  const handleTempRegistroFilterChange = (filter) => {
    setTempRegistroFilter(filter);
  }

  const handleClearTempRegistroFilter = () => {
    setTempRegistroFilter('todos');
  }

  const handleTempEstadoFilterChange = (filter) => {
    setTempEstadoFilter(filter);
  }

  const handleClearTempEstadoFilter = () => {
    setTempEstadoFilter('todos');
  }

  const handleTempBloqueadaFilterChange = (filter) => {
    setTempBloqueadaFilter(filter);
  }

  const handleClearTempBloqueadaFilter = () => {
    setTempBloqueadaFilter('todos');
  }

  const handleTempArchivadoFilterChange = (filter) => {
    setTempArchivadoFilter(filter);
  }

  const handleClearTempArchivadoFilter = () => {
    setTempArchivadoFilter('no');
  }

  const handleTempAssignedUserChange = (user: UserOption | null) => {
    setTempAssignedUser(user);
  };

  const handleClearTempAssignedUserFilter = () => {
    setTempAssignedUser(null);
  };

  const openFiltersModal = () => {
    // Sync temp filters with current applied filters when opening modal
    setTempDateFrom(dateFrom);
    setTempDateTo(dateTo);
    setTempDateField(dateField);
    setTempEscrituraFilter(escrituraFilter);
    setTempRegistroFilter(registroFilter);
    setTempEstadoFilter(estadoFilter);
    setTempBloqueadaFilter(bloqueadaFilter);
    setTempArchivadoFilter(archivadoFilter);
    setTempAssignedUser(assignedUser);
    setFiltersModalOpen(true);
  }

  const closeFiltersModal = () => {
    setFiltersModalOpen(false);
  }

  const handleApplyFilters = () => {
    // Set flag to prevent useEffect from triggering another fetch
    isApplyingFilters.current = true;

    // Apply temp filters using the new applyFilters function
    // This will update states and fetch data in a single operation
    applyFilters({
      dateFrom: tempDateFrom,
      dateTo: tempDateTo,
      dateField: tempDateField,
      escrituraFilter: tempEscrituraFilter,
      registroFilter: tempRegistroFilter,
      estadoFilter: tempEstadoFilter,
      bloqueadaFilter: tempBloqueadaFilter,
      archivadoFilter: tempArchivadoFilter,
      assignedUserId: tempAssignedUser?.id ?? '',
      assignedUser: tempAssignedUser,
    });

    // Reset flag after a short delay to allow state updates to propagate
    setTimeout(() => {
      isApplyingFilters.current = false;
    }, FILTERS.STATE_SYNC_DELAY);

    closeFiltersModal();
  }

  const handleClearAllFilters = () => {
    // Reset all filters and clear sessionStorage
    resetFilters();

    // Update temp filters to reflect the reset
    const hasDateFilterPermission = userPermissions.includes(PERMISSIONS.FILTER_BY_DATE.name);
    setTempDateFrom(hasDateFilterPermission ? FILTERS.DATE_FROM_DEFAULT : '');
    setTempDateTo('');
    setTempDateField('fecini');
    setTempEscrituraFilter('');
    setTempRegistroFilter('');
    setTempEstadoFilter('');
    setTempBloqueadaFilter('');
    setTempArchivadoFilter('');
    setTempAssignedUser(null);
  }

  // Check if any filters are active (only include estado filter if user can see status)
  const hasActiveFilters = !!(
    dateFrom ||
    dateTo ||
    (!isKanbanView && escrituraFilter && escrituraFilter !== 'todas') ||
    (registroFilter && registroFilter !== 'todos') ||
    (canSeeStatus && estadoFilter && estadoFilter !== 'todos') ||
    (canManageBloqueo && bloqueadaFilter && bloqueadaFilter !== 'todos') ||
    (canArchivar && archivadoFilter && archivadoFilter !== 'no') ||
    assignedUserId
  );

  const handleClickFinalizar = (idTramite) => {
    return () => {
      questionAlert({
        title: 'Finalizar trámite',
        text: `¿Esta seguro que desea finalizar el trámite?`,
        icon: 'question'
      }).then(r => {
        if (r.isConfirmed) {
          updateEstadoTramite({ id: idTramite, estado: 'finalizado' })
            .then(updated => {
              successAlert({ message: 'Trámite finalizado con éxito' });

              if (!isKanbanView) {
                fetchDataTramites();
                return;
              }

              // Con un filtro de estado activo que ya no lo incluye, la tarjeta
              // deja de pertenecer al tablero; si no, sólo cambió un campo.
              const estadoFiltraFinalizados =
                estadoFilter && estadoFilter !== 'todos' && estadoFilter !== 'finalizado';

              if (estadoFiltraFinalizados) {
                removeKanbanTramite(queryClient, idTramite);
                return;
              }

              applyTramiteUpdate(idTramite, updated ?? { estado: 'finalizado' });

              // Los finalizados viven en "Inscripta": la tarjeta cambia de
              // columna salvo que esté bloqueada.
              if (updated) {
                void relocateKanbanTramite(
                  queryClient,
                  idTramite,
                  getEscrituraKanbanStatus(updated),
                );
              }
            }).catch(err => {
              errorAlert({
                title: 'Error al actualizar trámite',
                exception: err
              });
            });
        }
      });
    }
  }

  const handleClickBloqueo = (tramite: any) => {
    if (tramite.bloqueado) {
      questionAlert({
        title: 'Desbloquear escritura',
        text: '¿Esta seguro que desea desbloquear esta escritura?',
        icon: 'question',
      }).then(r => {
        if (r.isConfirmed) {
          toggleBloqueo({ id: tramite.id, bloqueado: false })
            .then((updated) => applyBloqueoUpdate(tramite.id, updated))
            .catch(() => { /* el error ya se notifica en el hook */ });
        }
      });
      return;
    }

    setBloqueoDialogTramite(tramite);
  };

  /**
   * ¿El filtro de archivado activo deja de incluir a una escritura que quedó
   * con este valor? Se usa para decidir si la tarjeta sale del kanban o sólo
   * se actualiza (mismo criterio que `estadoFiltraFinalizados`).
   */
  const archivadoFiltraFuera = (archivado: boolean) => {
    const filtro = archivadoFilter || 'no';
    if (filtro === 'todas') return false;
    return filtro === 'si' ? !archivado : archivado;
  };

  const aplicarCambioDeArchivado = (tramiteId: number, archivado: boolean, updated?: unknown) => {
    if (!isKanbanView) {
      fetchDataTramites();
      return;
    }

    if (archivadoFiltraFuera(archivado)) {
      removeKanbanTramite(queryClient, tramiteId);
      return;
    }

    applyTramiteUpdate(tramiteId, updated ?? { archivado });
  };

  const handleClickArchivado = (tramite: any) => {
    if (tramite.archivado) {
      questionAlert({
        title: 'Desarchivar escritura',
        text: '¿Esta seguro que desea desarchivar esta escritura?',
        icon: 'question',
      }).then(r => {
        if (r.isConfirmed) {
          toggleArchivado({ id: tramite.id, archivado: false })
            .then((updated) => aplicarCambioDeArchivado(tramite.id, false, updated))
            .catch(() => { /* el error ya se notifica en el hook */ });
        }
      });
      return;
    }

    setArchivarDialogTramite(tramite);
  };

  const exitSelectionMode = () => {
    setSelectionMode(false);
    setSelectedIds(new Set());
  };

  const handleToggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleToggleAll = () => {
    const idsVisibles = (tramites ?? []).map((t: any) => t.id);
    const todasSeleccionadas = idsVisibles.length > 0 && idsVisibles.every((id) => selectedIds.has(id));
    setSelectedIds(todasSeleccionadas ? new Set() : new Set(idsVisibles));
  };

  const handleClearSelection = () => setSelectedIds(new Set());

  /**
   * Ids realmente enviables: se intersecta con lo que hay en pantalla para que
   * un id viejo nunca llegue al endpoint masivo, que es todo-o-nada y
   * rechazaría el lote entero.
   */
  const idsSeleccionadosVisibles: number[] = (tramites ?? [])
    .filter((t: any) => selectedIds.has(t.id))
    .map((t: any) => t.id);

  const bloqueadasSeleccionadas = (tramites ?? [])
    .filter((t: any) => selectedIds.has(t.id) && t.bloqueado)
    .length;

  const ejecutarArchivadoMasivo = async (archivado: boolean, motivo?: string) => {
    if (idsSeleccionadosVisibles.length === 0) return;

    try {
      await archivarMasivo({ ids: idsSeleccionadosVisibles, archivado, motivo });
      setArchivarLoteAbierto(false);
      setSelectedIds(new Set());
      fetchDataTramites();
      // El tablero no está montado acá, pero sí cacheado: sin esto el usuario
      // vuelve al kanban y ve escrituras que ya archivó. `resetQueries` deja
      // cada columna en su primera página (4 requests), en vez de revalidar
      // todas las tandas scrolleadas.
      void resetKanbanBoard(queryClient);
    } catch {
      // El error ya se notifica en el hook; la selección se conserva para reintentar.
    }
  };

  const handleDesarchivarSeleccionadas = () => {
    if (idsSeleccionadosVisibles.length === 0) return;

    questionAlert({
      title: 'Desarchivar escrituras',
      text: `¿Desea desarchivar ${idsSeleccionadosVisibles.length} escritura(s)?`,
      icon: 'question',
    }).then(r => {
      if (r.isConfirmed) {
        void ejecutarArchivadoMasivo(false);
      }
    });
  };

  const handleAssignedUserSave = async (tramite, assignedUser: UserOption | null) => {
    try {
      const updated = await partialUpdateTramite({
        id: tramite.id,
        assigned_user_ids: assignedUser ? [Number(assignedUser.id)] : [],
      });

      successAlert({
        message: assignedUser
          ? 'Responsable actualizado con éxito'
          : 'Responsable removido con éxito',
      });
      applyTramiteUpdate(tramite.id, updated);
    } catch (error) {
      errorAlert({
        title: 'Error al actualizar responsable',
        exception: error,
      });
      throw error;
    }
  };

  // La selección es por página: cualquier cosa que reemplace las filas
  // (paginar, filtrar, buscar, reordenar) la deja obsoleta, y un Set que
  // sobrevive haría mentir al contador y al checkbox del encabezado.
  useEffect(() => {
    setSelectedIds(new Set());
  }, [currentPage, activeSearchText, sortField, sortDirection, archivadoFilter, estadoFilter, registroFilter, bloqueadaFilter, dateFrom, dateTo, dateField, assignedUserId]);

  useEffect(() => {
    // Don't fetch if we're currently applying filters (applyFilters already fetches)
    // The kanban view feeds itself per column, so the paginated list is not needed
    if (isReady && !isKanbanView && !isApplyingFilters.current) {
      fetchDataTramites();
    }
  }, [sortField, sortDirection, fetchDataTramites, isReady, isKanbanView]);

  return (
    <AppLayout
      title="Escrituras"
      prevButtonVisible={false}
    >
      <div className="bg-white rounded-md shadow-md">
        <div>
          <div
            className={`px-2 py-2 flex-col gap-2 sm:flex-row justify-between items-stretch sm:items-center flex-wrap ${loading ? 'hidden' : 'flex'}`}
          >
            <div className='flex items-center gap-2 sm:gap-4 flex-wrap'>

              {canManageTramites && (
                <div className="flex gap-2">
                  <Button
                    asChild
                    className="font-semibold text-sm"
                  >
                    <ReactRouterLink to="/tramite">
                      Nuevo tramite
                    </ReactRouterLink>
                  </Button>
                </div>
              )}
              {/* Filters Button */}
              <div className="relative">
                <Button
                  variant={hasActiveFilters ? "default" : "outline"}
                  onClick={openFiltersModal}
                  className={`flex items-center gap-2 ${hasActiveFilters
                    ? 'bg-primary hover:bg-primary/90 text-white'
                    : ''
                    }`}
                >
                  <Filter className="h-4 w-4" />
                  <span className="text-xs">Filtros</span>
                  {hasActiveFilters && (
                    <span className="ml-1 bg-white text-blue-600 text-xs px-1.5 py-0.5 rounded-full font-medium">
                      Activos
                    </span>
                  )}
                </Button>

              </div>
              <div
                className="flex rounded-lg border bg-white p-0.5"
                role="group"
                aria-label="Seleccionar vista de escrituras"
              >
                <Button
                  variant={viewMode === 'table' ? 'default' : 'ghost'}
                  size="sm"
                  className="h-8 rounded-md px-2.5 text-xs"
                  onClick={() => handleViewModeChange('table')}
                  aria-pressed={viewMode === 'table'}
                  title="Vista de tabla"
                >
                  <List className="size-4" />
                  Tabla
                </Button>
                <Button
                  variant={viewMode === 'kanban' ? 'default' : 'ghost'}
                  size="sm"
                  className="h-8 rounded-md px-2.5 text-xs"
                  onClick={() => handleViewModeChange('kanban')}
                  aria-pressed={viewMode === 'kanban'}
                  title="Vista kanban"
                >
                  <LayoutDashboard className="size-4" />
                  Kanban
                </Button>
              </div>
              {canArchivar && !isKanbanView && (
                <Button
                  variant={selectionMode ? 'secondary' : 'outline'}
                  size="sm"
                  onClick={() => (selectionMode ? exitSelectionMode() : setSelectionMode(true))}
                  aria-pressed={selectionMode}
                  title={selectionMode ? 'Salir del modo selección' : 'Seleccionar escrituras para archivar'}
                >
                  {selectionMode ? <X className="size-4" /> : <CheckSquare className="size-4" />}
                  {selectionMode ? 'Cancelar' : 'Seleccionar'}
                </Button>
              )}
            </div>
            <div className="flex min-w-0 flex-wrap items-center py-2 gap-2 sm:gap-4 sm:flex-1 sm:basis-80 justify-end">
              <div className="flex min-w-[12rem] flex-1 basis-full sm:basis-48 items-center gap-2 sm:gap-4">
                <Input
                  onChange={(e) => setSearchText(e.target.value)}
                  value={searchText}
                  placeholder="Buscar trámites..."
                  onKeyPress={handleKeyPress}
                />
                <Button onClick={handleSearchClick} disabled={loading} className="px-5 shrink-0">
                  <Search className="h-4 w-4" />
                </Button>
              </div>
              {viewMode === 'table' && <ColumnToggle />}
              {checkAllPermissions(userPermissions, [PERMISSIONS.MANAGE_TRAMITE_TAREAS_BASE.name]) && (
                <Button variant="outline" size="sm" className="shrink-0" asChild>
                  <ReactRouterLink to={TRAMITE_TAREAS_ROUTES.BASES}>
                    <ListChecks className="mr-2 h-4 w-4" />
                    Tareas predeterminadas
                  </ReactRouterLink>
                </Button>
              )}
            </div>
          </div>



          {/* Filters Modal */}
          <FiltersModal
            open={filtersModalOpen}
            onClose={closeFiltersModal}
            dateFrom={tempDateFrom}
            dateTo={tempDateTo}
            dateField={tempDateField}
            onDateFromChange={handleTempDateFromChange}
            onDateToChange={handleTempDateToChange}
            onDateFieldChange={handleTempDateFieldChange}
            onClearDateFilter={handleClearTempDateFilter}
            escrituraFilter={tempEscrituraFilter}
            onEscrituraFilterChange={handleTempEscrituraFilterChange}
            onClearEscrituraFilter={handleClearTempEscrituraFilter}
            registroFilter={tempRegistroFilter}
            onRegistroFilterChange={handleTempRegistroFilterChange}
            onClearRegistroFilter={handleClearTempRegistroFilter}
            estadoFilter={tempEstadoFilter}
            onEstadoFilterChange={handleTempEstadoFilterChange}
            onClearEstadoFilter={handleClearTempEstadoFilter}
            bloqueadaFilter={tempBloqueadaFilter}
            onBloqueadaFilterChange={handleTempBloqueadaFilterChange}
            onClearBloqueadaFilter={handleClearTempBloqueadaFilter}
            archivadoFilter={tempArchivadoFilter}
            onArchivadoFilterChange={handleTempArchivadoFilterChange}
            onClearArchivadoFilter={handleClearTempArchivadoFilter}
            assignedUser={tempAssignedUser}
            onAssignedUserChange={handleTempAssignedUserChange}
            onClearAssignedUserFilter={handleClearTempAssignedUserFilter}
            onApplyFilters={handleApplyFilters}
            onClearAllFilters={handleClearAllFilters}
            showEscrituraFilter={!isKanbanView}
          />

          <BloqueoTramiteDialog
            tramite={bloqueoDialogTramite}
            isOpen={Boolean(bloqueoDialogTramite)}
            onClose={() => setBloqueoDialogTramite(null)}
            onBlocked={(updated) => applyBloqueoUpdate(updated.id, updated)}
          />

          {/* Barra de acciones masivas */}
          {selectionMode && !isKanbanView && (
            <div className="mx-2 mb-2 flex flex-wrap items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2">
              <span className="text-sm font-medium text-blue-800">
                {idsSeleccionadosVisibles.length} escritura{idsSeleccionadosVisibles.length !== 1 ? 's' : ''} seleccionada{idsSeleccionadosVisibles.length !== 1 ? 's' : ''}
              </span>
              <Button
                size="sm"
                disabled={idsSeleccionadosVisibles.length === 0 || isArchivando}
                onClick={() => setArchivarLoteAbierto(true)}
              >
                <Archive className="h-4 w-4" />
                Archivar seleccionadas
              </Button>
              {(archivadoFilter === 'si' || archivadoFilter === 'todas') && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={idsSeleccionadosVisibles.length === 0 || isArchivando}
                  onClick={handleDesarchivarSeleccionadas}
                >
                  <ArchiveRestore className="h-4 w-4" />
                  Desarchivar seleccionadas
                </Button>
              )}
              <button
                type="button"
                className="text-sm text-blue-600 hover:underline disabled:opacity-50"
                disabled={selectedIds.size === 0}
                onClick={handleClearSelection}
              >
                Limpiar selección
              </button>
              <button
                type="button"
                className="ml-auto text-sm text-blue-600 hover:underline"
                onClick={exitSelectionMode}
              >
                Salir
              </button>
            </div>
          )}

          <ArchivarTramiteDialog
            isOpen={Boolean(archivarDialogTramite)}
            onClose={() => setArchivarDialogTramite(null)}
            isSubmitting={isArchivando}
            bloqueadasCount={archivarDialogTramite?.bloqueado ? 1 : 0}
            onConfirm={async (motivo) => {
              if (!archivarDialogTramite) return;
              try {
                const updated = await toggleArchivado({
                  id: archivarDialogTramite.id,
                  archivado: true,
                  motivo,
                });
                aplicarCambioDeArchivado(archivarDialogTramite.id, true, updated);
                setArchivarDialogTramite(null);
              } catch {
                // El error ya se notifica en el hook
              }
            }}
          />

          <ArchivarTramiteDialog
            isOpen={archivarLoteAbierto}
            onClose={() => setArchivarLoteAbierto(false)}
            count={idsSeleccionadosVisibles.length}
            bloqueadasCount={bloqueadasSeleccionadas}
            isSubmitting={isArchivando}
            onConfirm={(motivo) => ejecutarArchivadoMasivo(true, motivo)}
          />

          {/* Results Counter */}
          {!isKanbanView && (
            <div className="px-1 pb-2">
              <ResultsCounter
                totalItems={totalItems}
                from={from}
                to={to}
                currentPage={currentPage}
                loading={loading}
                searchText={searchText}
                hasFilters={hasActiveFilters}
              />
            </div>
          )}
        </div>

        {loading && !isKanbanView && <Progress value={undefined} className="h-1" />}
        <div className={!isKanbanView && isLargerThan1280 ? 'bg-white' : ''}>
          {isKanbanView ? (
            <TramitesKanban
              filters={kanbanFilters}
              onAssignedUserSave={handleAssignedUserSave}
            />
          ) : (
            <>
              {isLargerThan1280 && <TramitesTable
                loading={loading}
                tramites={tramites}
                currentPage={currentPage}
                handleClickFinalizar={handleClickFinalizar}
                onClickBloqueo={handleClickBloqueo}
                onClickArchivado={handleClickArchivado}
                selectionMode={selectionMode}
                selectedIds={selectedIds}
                onToggleSelect={handleToggleSelect}
                onToggleAll={handleToggleAll}
                sortConfig={{ field: sortField as any, direction: sortDirection as any }}
                onSortChanged={handleSortChanged}
                onAssignedUserSave={handleAssignedUserSave}
                onTareasChanged={fetchDataTramites}
              />}
              {!isLargerThan1280 && <TramitesFichas
                loading={loading}
                tramites={tramites}
                handleClickFinalizar={handleClickFinalizar}
              />}
            </>
          )}
          {!isKanbanView && <Paginator links={links} />}
        </div>
      </div>
    </AppLayout>
  );
}

export default Home;
